import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BookOpen,
  Dumbbell,
  UtensilsCrossed,
  TrendingUp,
  FileText,
  ArrowLeft,
  Calendar,
  Target,
  MessageSquare,
  Settings,
} from 'lucide-react';
import * as planService from '../../services/planService';
import { useAuth } from '../../contexts/AuthContext';
import WorkoutCalendar from './TrainingProtocol/WorkoutCalendar';
import ExerciseLibrary from './TrainingProtocol/ExerciseLibrary';
import MealPlanView from './NutritionPlan/MealPlanView';
import RecipeLibrary from './NutritionPlan/RecipeLibrary';
import ProgressDashboard from './ProgressTracking/ProgressDashboard';
import CoachMessaging from './Communication/CoachMessaging';
import CoachNotes from './Communication/CoachNotes';
import VideoCallScheduler from './Communication/VideoCallScheduler';

/**
 * Plan View Component
 * Individual plan detail view with tabbed navigation
 */
export default function PlanView() {
  const { planId } = useParams();
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (planId) {
      loadPlan();
    }
  }, [planId]);

  const loadPlan = async () => {
    setIsLoading(true);
    setError('');

    try {
      const result = await planService.getPlanDetails(planId);
      if (result.success) {
        setPlan(result.data);
      } else {
        setError(result.error || 'Failed to load plan');
      }
    } catch (err) {
      console.error('Error loading plan:', err);
      setError('Failed to load plan. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BookOpen },
    { id: 'training', label: 'Training', icon: Dumbbell },
    { id: 'nutrition', label: 'Nutrition', icon: UtensilsCrossed },
    { id: 'progress', label: 'Progress', icon: TrendingUp },
    { id: 'communication', label: 'Communication', icon: MessageSquare },
    { id: 'resources', label: 'Resources', icon: FileText },
  ];

  // Filter tabs based on configuration
  const availableTabs = tabs.filter(tab => {
    if (!plan?.configuration) return true;
    const config = plan.configuration;
    switch (tab.id) {
      case 'training':
        return config.enable_training !== false;
      case 'nutrition':
        return config.enable_nutrition !== false;
      case 'progress':
        return config.enable_progress_tracking !== false;
      case 'resources':
        return config.enable_resources !== false;
      case 'communication':
        return config.enable_messaging !== false;
      default:
        return true;
    }
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading plan...</p>
        </div>
      </div>
    );
  }

  if (error || !plan) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <p className="text-red-600">{error || 'Plan not found'}</p>
        <Link
          to="/account/programs"
          className="mt-4 inline-block text-primary-600 hover:text-primary-700 font-medium"
        >
          ← Back to Plans
        </Link>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Link
          to="/account/programs"
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Plans</span>
        </Link>
      </div>

      {/* Plan Header */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{plan.program_name}</h1>
            <p className="text-gray-600 capitalize">{plan.program_type.replace('_', ' ')}</p>
          </div>
          <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(plan.status)}`}>
            {plan.status}
          </span>
        </div>

        {/* Plan Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Start Date</p>
              <p className="font-medium text-gray-900">
                {new Date(plan.start_date).toLocaleDateString()}
              </p>
            </div>
          </div>
          {plan.end_date && (
            <div className="flex items-center gap-3">
              <Target className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">End Date</p>
                <p className="font-medium text-gray-900">
                  {new Date(plan.end_date).toLocaleDateString()}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Coach Notes */}
        {plan.coach_notes && (
          <div className="mt-4 p-4 bg-primary-50 rounded-lg border border-primary-200">
            <p className="text-sm font-medium text-primary-900 mb-1">Coach Notes:</p>
            <p className="text-sm text-primary-800">{plan.coach_notes}</p>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-lg">
        <div className="border-b border-gray-200">
          <div className="flex overflow-x-auto space-x-1 px-6" role="tablist" aria-label="Plan navigation tabs">
            {availableTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap border-b-2 touch-manipulation ${
                    activeTab === tab.id
                      ? 'text-primary-600 border-primary-600'
                      : 'text-gray-600 border-transparent hover:text-gray-900'
                  }`}
                  aria-label={`Switch to ${tab.label} tab`}
                  aria-selected={activeTab === tab.id}
                  role="tab"
                  type="button"
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'overview' && <OverviewTab plan={plan} />}
          {activeTab === 'training' && <TrainingTab plan={plan} />}
          {activeTab === 'nutrition' && <NutritionTab plan={plan} />}
          {activeTab === 'progress' && <ProgressTab plan={plan} />}
          {activeTab === 'communication' && <CommunicationTab plan={plan} />}
          {activeTab === 'resources' && <ResourcesTab plan={plan} />}
        </div>
      </div>
    </div>
  );
}

// Overview Tab
function OverviewTab({ plan }) {
  const workouts = plan.workouts || [];
  const meals = plan.meals || [];
  const checkpoints = plan.checkpoints || [];
  const completedCheckpoints = checkpoints.filter(c => c.is_completed).length;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Plan Overview</h2>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Total Workouts</p>
          <p className="text-2xl font-bold text-gray-900">{workouts.length}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Meal Plans</p>
          <p className="text-2xl font-bold text-gray-900">{meals.length}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Checkpoints</p>
          <p className="text-2xl font-bold text-gray-900">
            {completedCheckpoints} / {checkpoints.length}
          </p>
        </div>
      </div>

      {/* Upcoming Checkpoints */}
      {checkpoints.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Checkpoints</h3>
          <div className="space-y-2">
            {checkpoints
              .filter(c => !c.is_completed)
              .slice(0, 3)
              .map((checkpoint) => (
                <div key={checkpoint.id} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{checkpoint.checkpoint_name}</p>
                      {checkpoint.target_date && (
                        <p className="text-sm text-gray-600">
                          Target: {new Date(checkpoint.target_date).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <Clock className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Training Tab
function TrainingTab({ plan }) {
  const [trainingView, setTrainingView] = useState('calendar'); // 'calendar' or 'library'

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Training Protocol</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setTrainingView('calendar')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors touch-manipulation min-h-[44px] ${
              trainingView === 'calendar'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            type="button"
            aria-label="Switch to calendar view"
            aria-pressed={trainingView === 'calendar'}
          >
            Calendar
          </button>
          <button
            onClick={() => setTrainingView('library')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors touch-manipulation min-h-[44px] ${
              trainingView === 'library'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            type="button"
            aria-label="Switch to exercise library view"
            aria-pressed={trainingView === 'library'}
          >
            Exercise Library
          </button>
        </div>
      </div>

      {trainingView === 'calendar' ? (
        <WorkoutCalendar planId={plan.id} />
      ) : (
        <ExerciseLibrary planId={plan.id} />
      )}
    </div>
  );
}

// Nutrition Tab
function NutritionTab({ plan }) {
  const [nutritionView, setNutritionView] = useState('meals'); // 'meals' or 'recipes'

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Nutrition Plan</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setNutritionView('meals')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors touch-manipulation min-h-[44px] ${
              nutritionView === 'meals'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            type="button"
            aria-label="Switch to meal plan view"
            aria-pressed={nutritionView === 'meals'}
          >
            Meal Plan
          </button>
          <button
            onClick={() => setNutritionView('recipes')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors touch-manipulation min-h-[44px] ${
              nutritionView === 'recipes'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            type="button"
            aria-label="Switch to recipe library view"
            aria-pressed={nutritionView === 'recipes'}
          >
            Recipes
          </button>
        </div>
      </div>

      {nutritionView === 'meals' ? (
        <MealPlanView planId={plan.id} />
      ) : (
        <RecipeLibrary planId={plan.id} />
      )}
    </div>
  );
}

// Progress Tab
function ProgressTab({ plan }) {
  return <ProgressDashboard planId={plan.id} />;
}

// Communication Tab
function CommunicationTab({ plan }) {
  const [communicationView, setCommunicationView] = useState('notes'); // 'notes', 'messaging', 'scheduler'

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Communication</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCommunicationView('notes')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors touch-manipulation min-h-[44px] ${
              communicationView === 'notes'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            type="button"
            aria-label="View coach notes"
            aria-pressed={communicationView === 'notes'}
          >
            Coach Notes
          </button>
          <button
            onClick={() => setCommunicationView('messaging')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors touch-manipulation min-h-[44px] ${
              communicationView === 'messaging'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            type="button"
            aria-label="View messages"
            aria-pressed={communicationView === 'messaging'}
          >
            Messages
          </button>
          <button
            onClick={() => setCommunicationView('scheduler')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors touch-manipulation min-h-[44px] ${
              communicationView === 'scheduler'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            type="button"
            aria-label="Schedule video call"
            aria-pressed={communicationView === 'scheduler'}
          >
            Schedule Call
          </button>
        </div>
      </div>

      {communicationView === 'notes' && <CoachNotes planId={plan.id} />}
      {communicationView === 'messaging' && <CoachMessaging planId={plan.id} />}
      {communicationView === 'scheduler' && <VideoCallScheduler planId={plan.id} />}
    </div>
  );
}

// Resources Tab
function ResourcesTab({ plan }) {
  const resources = plan.resources || [];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Resources</h2>
      {resources.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {resources.map((resource) => (
            <div key={resource.id} className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">{resource.resource_name}</h3>
              <p className="text-sm text-gray-600 mb-2">{resource.description}</p>
              {resource.url && (
                <a
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  View Resource →
                </a>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-600">No resources available for this plan.</p>
      )}
    </div>
  );
}
